import React from 'react';
import { ChevronDownIcon, ChevronRightIcon, PlayIcon } from '@heroicons/react/24/outline';
import GroupStatusSidebarFilters, { MODEL_PARENT_STATUS_FILTERS } from './GroupStatusSidebarFilters';

export function groupsHiddenByModelParents(models) {
  const hidden = new Set();
  for (const model of models || []) {
    for (const group of model.groups || []) {
      const name = typeof group === 'string' ? group : group?.name;
      if (name) hidden.add(name);
    }
  }
  return hidden;
}

function statusSlice(row) {
  return {
    active: row?.active || 0,
    suspended: row?.suspended || 0,
    captcha: row?.captcha || 0,
    auth: row?.auth || 0
  };
}

/**
 * Model parents in the accounts / chatbot group sidebar.
 * Same row language as the flat groups: name, total, then the status rows
 * you can click to filter. Nested groups repeat that pattern.
 */
export default function ModelParentSidebar({
  models,
  collapsed,
  onToggle,
  selectedModelId,
  selectedGroup,
  filterStatus,
  filterVpsAgentId,
  onSelectModel,
  onSelectModelStatus,
  onSelectGroup,
  onSelectGroupStatus,
  onSelectGroupVps,
  onStartModel
}) {
  if (!models || models.length === 0) return null;

  const modelSelected = (modelId) => String(selectedModelId) === String(modelId);
  const statusIsAll = !filterStatus || filterStatus === 'all';
  const groupIsAll = !selectedGroup || selectedGroup === 'all';
  const vpsIsAll = !filterVpsAgentId || filterVpsAgentId === 'all';

  return (
    <>
      {models.map((model) => {
        const isCollapsed = !!(collapsed && collapsed[model.modelId]);
        const modelName = model.modelName || model.modelId;
        const thisModel = modelSelected(model.modelId);
        const parentRowSelected = thisModel && groupIsAll && statusIsAll && vpsIsAll;
        const groups = (model.groups || []).map((group) => (
          typeof group === 'string'
            ? { name: group, total: 0, active: 0, suspended: 0, captcha: 0, auth: 0 }
            : group
        )).filter((group) => group?.name);

        return (
          <div key={model.modelId} className="pt-0.5">
            <div className={`flex items-center rounded-md transition-colors ${
              parentRowSelected
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}>
              <button
                type="button"
                onClick={() => onToggle(model.modelId)}
                className="pl-2 pr-0.5 py-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 shrink-0"
                title={isCollapsed ? 'Expand' : 'Collapse'}
                aria-label={isCollapsed ? `Expand ${modelName}` : `Collapse ${modelName}`}
              >
                {isCollapsed
                  ? <ChevronRightIcon className="h-3.5 w-3.5" />
                  : <ChevronDownIcon className="h-3.5 w-3.5" />}
              </button>
              <button
                type="button"
                onClick={() => onSelectModel(model)}
                className="flex-1 min-w-0 text-left py-2 pr-2 text-sm font-medium flex items-center justify-between gap-2"
                title={`Show all ${modelName} accounts`}
              >
                <span className="truncate">{modelName}</span>
                <span className="text-xs tabular-nums text-gray-400 dark:text-gray-500 shrink-0">({model.total || 0})</span>
              </button>
              {onStartModel && (
                <button
                  type="button"
                  onClick={() => onStartModel(model)}
                  className="pr-2 py-2 text-green-600 dark:text-green-400 hover:text-green-500 shrink-0"
                  title={`Start chatbot automation for logged-in ${modelName} accounts`}
                >
                  <PlayIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {!isCollapsed && (
              <>
                <GroupStatusSidebarFilters
                  filters={MODEL_PARENT_STATUS_FILTERS}
                  counts={statusSlice(model)}
                  isActive={(status) => thisModel && groupIsAll && vpsIsAll && filterStatus === status}
                  onSelect={(_key, status) => onSelectModelStatus(model, status)}
                />
                {groups.length > 0 && (
                  <div className="ml-3 space-y-0.5 border-l border-gray-200 dark:border-gray-700 pl-1">
                    {groups.map((group) => {
                      const groupSelected = thisModel && selectedGroup === group.name && statusIsAll && vpsIsAll;
                      const counts = statusSlice(group);
                      return (
                        <div key={group.name}>
                          <button
                            type="button"
                            onClick={() => onSelectGroup(model, group.name)}
                            className={`w-full text-left px-2 py-1.5 text-sm rounded-md transition-colors flex items-center justify-between gap-2 ${
                              groupSelected
                                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                            title={group.name}
                          >
                            <span className="truncate">{group.name}</span>
                            <span className="text-xs tabular-nums text-gray-400 dark:text-gray-500 shrink-0">({group.total || 0})</span>
                          </button>
                          <div className="ml-3 mt-0.5 mb-1 space-y-0.5 border-l border-gray-200 dark:border-gray-700 pl-2">
                            {MODEL_PARENT_STATUS_FILTERS.map((item) => {
                              const bucket = item.countKey || item.status;
                              const n = counts[bucket] || 0;
                              const vpsRows = (group.vps && group.vps[bucket]) || [];
                              const statusActive = thisModel && selectedGroup === group.name && vpsIsAll && filterStatus === item.status;
                              return (
                                <div key={item.status}>
                                  <button
                                    type="button"
                                    title={item.title || item.label}
                                    onClick={() => onSelectGroupStatus(model, group.name, item.status)}
                                    className={`w-full text-left px-2 py-1 text-[11px] rounded-md transition-colors flex items-center justify-between ${
                                      statusActive
                                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                                  >
                                    <span>{item.label}</span>
                                    <span className="tabular-nums text-gray-400 dark:text-gray-500">({n})</span>
                                  </button>
                                  {n > 0 && vpsRows.length > 0 && (
                                    <div className="ml-2 mb-0.5 space-y-0.5 border-l border-gray-200 dark:border-gray-700 pl-1.5">
                                      {vpsRows.map((vps) => {
                                        const vpsActive = thisModel
                                          && selectedGroup === group.name
                                          && filterStatus === item.status
                                          && String(filterVpsAgentId) === String(vps.id);
                                        return (
                                          <button
                                            key={vps.id}
                                            type="button"
                                            onClick={() => onSelectGroupVps(model, group.name, item.status, vps.id)}
                                            title={`${vps.name} · ${item.label}`}
                                            className={`w-full text-left px-1.5 py-0.5 text-[10px] rounded-md transition-colors flex items-center justify-between gap-1 ${
                                              vpsActive
                                                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                                                : 'text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                                            }`}
                                          >
                                            <span className="truncate">{vps.name}</span>
                                            <span className="tabular-nums shrink-0">({vps.count})</span>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </>
  );
}
